import { useEffect, useRef, useCallback, useState } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  delay?: number;
}

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollRevealOptions = {}
) {
  const { threshold = 0.12, rootMargin = '0px 0px -40px 0px', delay = 0 } = options;
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  const setRef = useCallback((node: T | null) => {
    ref.current = node;
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setRevealed(true), delay);
          observer.unobserve(node);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, delay]);

  return { ref: setRef, revealed };
}

export function useScrollRevealGroup<T extends HTMLElement = HTMLDivElement>(
  itemCount: number,
  options: UseScrollRevealOptions = {}
) {
  const { threshold = 0.1, rootMargin = '0px 0px -30px 0px', delay = 0 } = options;
  const containerRef = useRef<T | null>(null);
  const [revealedCount, setRevealedCount] = useState(0);

  const setContainerRef = useCallback((node: T | null) => {
    containerRef.current = node;
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const interval = setInterval(() => {
            setRevealedCount((prev) => {
              if (prev >= itemCount) {
                clearInterval(interval);
                observer.unobserve(node);
                return prev;
              }
              return prev + 1;
            });
          }, 120);
          return () => clearInterval(interval);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [itemCount, threshold, rootMargin]);

  return { containerRef: setContainerRef, revealedCount };
}
